<template>
  <div class="p-3 bg-gray-50 dark:bg-gray-900 min-h-screen">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-4">
      <div>
        <h1 class="text-xl font-bold text-gray-800 dark:text-white">Gestión de Productos</h1>
        <p class="text-xs text-gray-600 dark:text-gray-400">Administra el inventario</p>
      </div>
      <div class="flex items-center gap-2">
        <button v-if="filteredProductos.length > 0" @click="abrirModalRecalcularMasivo" class="flex items-center px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all text-xs font-semibold shadow hover:shadow-md">
          <Icon icon="mdi:calculator" class="mr-1.5" />
          <span>Recalcular</span>
        </button>
        <button @click="openCreateModal" class="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-xs font-semibold shadow hover:shadow-md">
          <Icon icon="mdi:plus" class="mr-1.5" />
          <span>Nuevo</span>
        </button>
      </div>
    </div>

    <!-- LECTOR DE CÓDIGO - BÚSQUEDA RÁPIDA -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-3">
        <div class="flex-1 relative">
          <Icon icon="mdi:barcode-scan" class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-xl" />
          <input 
            ref="lectorInput"
            v-model="codigoEscaneado"
            type="text"
            placeholder="Escanea o ingresa el código del producto..."
            @keyup.enter="buscarProductoPorCodigo"
            class="w-full pl-11 pr-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-2 border-blue-200 dark:border-blue-800 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
            :disabled="buscando"
            autofocus
          />
          <div v-if="buscando" class="absolute right-3 top-1/2 -translate-y-1/2">
            <div class="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
        <button 
          @click="buscarProductoPorCodigo" 
          :disabled="!codigoEscaneado.trim() || buscando"
          class="px-5 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold shadow-lg shadow-blue-500/20"
        >
          <Icon :icon="buscando ? 'mdi:loading' : 'mdi:magnify'" :class="buscando ? 'animate-spin' : ''" />
          {{ buscando ? 'Buscando...' : 'Buscar' }}
        </button>
        <button 
          v-if="productoEncontrado || busquedaFallida"
          @click="limpiarBusqueda"
          class="px-4 py-3 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
        >
          <Icon icon="mdi:close" class="text-xl" />
        </button>
      </div>

      <!-- Resultado de búsqueda -->
      <div v-if="productoEncontrado" class="mt-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
        <div class="flex items-start gap-4">
          <div class="w-20 h-20 rounded-lg overflow-hidden bg-white flex-shrink-0 border border-gray-200">
            <img 
              v-if="getImageUrl(productoEncontrado.imagen)" 
              :src="getImageUrl(productoEncontrado.imagen)" 
              :alt="productoEncontrado.nombre"
              class="w-full h-full object-cover"
              @error="handleImageError"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
              <Icon icon="mdi:package-variant" class="text-3xl" />
            </div>
          </div>
          <div class="flex-1">
            <div class="flex items-start justify-between">
              <div>
                <h4 class="font-bold text-gray-800 dark:text-white">{{ productoEncontrado.nombre }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  <span class="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">{{ productoEncontrado.codigo }}</span>
                  <span v-if="productoEncontrado.codigoBarras" class="ml-1 bg-blue-100 dark:bg-blue-900 px-2 py-0.5 rounded text-xs font-mono text-blue-700 dark:text-blue-300">
                    <Icon icon="mdi:barcode-scan" class="inline" /> {{ productoEncontrado.codigoBarras }}
                  </span>
                  <span class="mx-2">|</span>
                  {{ productoEncontrado.marca || 'Sin marca' }}
                  <span v-if="productoEncontrado.modelo">- {{ productoEncontrado.modelo }}</span>
                </p>
              </div>
              <span :class="['px-3 py-1 rounded-full text-xs font-bold', productoEncontrado.estado?.toLowerCase() === 'activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700']">
                {{ productoEncontrado.estado }}
              </span>
            </div>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
              <div>
                <span class="text-gray-500">Stock:</span>
                <span :class="['font-bold', getStockClass(productoEncontrado.stock, productoEncontrado.stock_minimo)]">
                  {{ productoEncontrado.stock || 0 }}
                </span>
              </div>
              <div>
                <span class="text-gray-500">Precio:</span>
                <span class="font-bold text-green-600">${{ formatNumber(productoEncontrado.precio_venta_usd, 2) }}</span>
              </div>
              <div>
                <span class="text-gray-500">Categoría:</span>
                <span>{{ productoEncontrado.nombreCategoria || 'Sin cat.' }}</span>
              </div>
              <div>
                <span class="text-gray-500">Proveedor:</span>
                <span>{{ productoEncontrado.nombreProveedor || 'Sin prov.' }}</span>
              </div>
            </div>
            <div class="mt-2 flex gap-2">
              <button @click="openEditModal(productoEncontrado)" class="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600 transition-all flex items-center gap-1">
                <Icon icon="mdi:pencil" /> Editar
              </button>
              <button @click="openStockModal(productoEncontrado)" class="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs hover:bg-amber-600 transition-all flex items-center gap-1">
                <Icon icon="mdi:package-up" /> Ajustar Stock
              </button>
              <button @click="copiarProducto(productoEncontrado)" class="px-3 py-1.5 bg-purple-500 text-white rounded-lg text-xs hover:bg-purple-600 transition-all flex items-center gap-1">
                <Icon icon="mdi:content-copy" /> Duplicar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Producto no encontrado -->
      <div v-else-if="busquedaFallida" class="mt-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 text-center">
        <Icon icon="mdi:alert-circle" class="text-red-500 text-2xl mb-1" />
        <p class="text-red-600 dark:text-red-400 font-medium">Producto no encontrado</p>
        <p class="text-xs text-gray-500 mt-1">El código "{{ codigoEscaneado }}" no existe en el inventario</p>
        <button @click="abrirCrearDesdeCodigoEscaneado" class="mt-2 px-4 py-1.5 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600 transition-all">
          <Icon icon="mdi:plus" /> Crear con este código
        </button>
      </div>
    </div>

    <!-- Filtros Compactos -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-2 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
        <div class="md:col-span-4">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Buscar Producto</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Código, nombre, marca..." @input="handleSearch" class="w-full pl-11 pr-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" />
          </div>
        </div>
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Estado</label>
          <select v-model="filterStatus" @change="handleFilterChange" class="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:border-blue-500 outline-none transition-all cursor-pointer dark:text-white">
            <option value="all" class="dark:bg-gray-800 dark:text-white">Todos</option>
            <option value="activos" class="dark:bg-gray-800 dark:text-white">Activos</option>
            <option value="inactivos" class="dark:bg-gray-800 dark:text-white">Inactivos</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Stock</label>
          <select v-model="filterStock" class="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:border-blue-500 outline-none transition-all cursor-pointer dark:text-white">
            <option value="all" class="dark:bg-gray-800 dark:text-white">Cualquier Stock</option>
            <option value="con_stock" class="dark:bg-gray-800 dark:text-white">Con Stock</option>
            <option value="stock_cero" class="dark:bg-gray-800 dark:text-white">Sin Stock</option>
            <option value="stock_bajo" class="dark:bg-gray-800 dark:text-white">Stock Bajo</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Proveedor</label>
          <select v-model="filterProveedor" class="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:border-blue-500 outline-none transition-all cursor-pointer dark:text-white">
            <option value="all" class="dark:bg-gray-800 dark:text-white">Todos</option>
            <option v-for="prov in proveedores" :key="prov.idProveedor" :value="prov.idProveedor" class="dark:bg-gray-800 dark:text-white">{{ prov.nombre }}</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Ordenar</label>
          <select v-model="sortBy" class="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:border-blue-500 outline-none transition-all cursor-pointer dark:text-white">
            <option value="nombre" class="dark:bg-gray-800 dark:text-white">Nombre (A-Z)</option>
            <option value="precioVenta_desc" class="dark:bg-gray-800 dark:text-white">Precio Máximo</option>
            <option value="stock_desc" class="dark:bg-gray-800 dark:text-white">Mayor Stock</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Tasas -->
    <div v-if="tasaUSDT && tasaBCV" class="grid grid-cols-2 gap-3 mb-4">
      <div class="bg-blue-600 text-white p-2 rounded-xl flex items-center justify-between shadow-sm border border-blue-500">
        <div class="flex items-center gap-2">
          <div class="p-1.5 bg-white/20 rounded-lg"><Icon icon="mdi:currency-usdt" class="text-lg" /></div>
          <div><p class="text-[10px] opacity-80 leading-tight">Tasa USDT</p><p class="text-sm font-bold leading-tight">{{ formatNumber(tasaUSDT?.precio_usdt_bs, 2) }} Bs</p></div>
        </div>
        <div class="text-right"><p class="text-[9px] opacity-70">{{ tasaUSDT?.fuente }}</p></div>
      </div>
      <div class="bg-amber-600 text-white p-2 rounded-xl flex items-center justify-between shadow-sm border border-amber-500">
        <div class="flex items-center gap-2">
          <div class="p-1.5 bg-white/20 rounded-lg"><Icon icon="mdi:bank" class="text-lg" /></div>
          <div><p class="text-[10px] opacity-80 leading-tight">Tasa BCV</p><p class="text-sm font-bold leading-tight">{{ formatNumber(tasaBCV?.precio_usd_bs, 2) }} Bs</p></div>
        </div>
        <div class="text-right"><p class="text-[9px] opacity-70">{{ tasaBCV?.fuente }}</p></div>
      </div>
    </div>

    <!-- Lista Productos -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
        <div>
          <h2 class="text-base font-bold text-gray-800 dark:text-white leading-none mb-1">Productos</h2>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium leading-none">{{ filteredProductos.length }} productos registrados</p>
        </div>
        <div class="text-xs text-gray-400 font-medium"><Icon icon="mdi:calendar" class="inline mb-0.5" /> {{ formatTime(lastUpdate) }}</div>
      </div>

      <div v-if="loading" class="loading-spinner"><div class="spinner"></div><p>Cargando...</p></div>

      <div v-else-if="filteredProductos.length === 0" class="empty-state">
        <Icon icon="mdi:package-variant-off" />
        <h3>No hay productos</h3>
        <p>{{ searchQuery || filterStatus !== 'all' ? 'Cambia los filtros' : 'Registra el primero' }}</p>
        <button v-if="!searchQuery && filterStatus === 'all'" @click="openCreateModal">Registrar</button>
        <button v-else @click="clearFilters">Limpiar</button>
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 p-3">
        <div v-for="producto in paginatedProductos" :key="producto.idProducto" class="producto-card">
          <div class="producto-imagen">
            <span @click="abrirModalToggleStatus(producto)" :class="['estado-badge', producto.estado?.toLowerCase() === 'activo' ? 'estado-activo' : 'estado-inactivo']">
              <Icon :icon="producto.estado?.toLowerCase() === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" />
              {{ producto.estado === 'Activo' ? 'Activo' : 'Inactivo' }}
            </span>
            <div class="imagen-contenedor">
              <img v-if="getImageUrl(producto.imagen)" :src="getImageUrl(producto.imagen)" :alt="producto.nombre" @error="handleImageError" />
              <Icon v-else icon="mdi:package-variant" />
            </div>
            <span :class="['stock-badge', getStockClass(producto.stock, producto.stock_minimo)]">Stock: {{ producto.stock || 0 }}</span>
          </div>
          <div class="producto-info">
            <div class="producto-codigo">
              <Icon icon="mdi:barcode" /> {{ producto.codigo }}
              <span v-if="producto.codigoBarras" class="ml-1 text-blue-600 dark:text-blue-400 text-[10px]">
                <Icon icon="mdi:barcode-scan" class="inline" /> {{ producto.codigoBarras }}
              </span>
              <span class="categoria-badge"><Icon icon="mdi:folder" /> {{ producto.nombreCategoria || 'Sin cat.' }}</span>
            </div>
            <h3>{{ producto.nombre }}</h3>
            <p>{{ producto.marca || 'Sin marca' }} - {{ producto.modelo || 'Sin modelo' }}</p>
            <div class="precios">
              <div><span>Compra:</span><span>{{ formatNumber(producto.precio_compra_usdt, 2) }}$ <small>{{ formatBolivares(producto.precio_compra_usdt, 'compra') }}</small></span></div>
              <div><span>Venta:</span><span class="precio-venta">{{ formatNumber(producto.precio_venta_usd, 2) }}$ <small>{{ formatBolivares(producto.precio_venta_usd, 'venta') }}</small></span></div>
              <div><span>Ganancia:</span><span :class="calcularDiferencia(producto.precio_compra_usdt, producto.precio_venta_usd) > 0 ? 'ganancia-positiva' : 'ganancia-negativa'">{{ formatNumber(calcularDiferencia(producto.precio_compra_usdt, producto.precio_venta_usd), 2) }}$</span></div>
              <div class="margen"><span>Margen:</span><span>{{ calcularPorcentajeGanancia(producto.precio_compra_usdt, producto.precio_venta_usd) }}%</span></div>
            </div>
            <div class="producto-detalles">
              <div><Icon icon="mdi:account" /> {{ producto.nombreProveedor || 'Sin proveedor' }}</div>
              <div><Icon icon="mdi:calendar" /> {{ formatDate(producto.fecha_creacion, false) }}</div>
            </div>
            <div class="producto-acciones">
              <button @click.stop="openEditModal(producto)" class="btn-editar" title="Editar"><Icon icon="mdi:pencil" /></button>
              <button @click.stop="openStockModal(producto)" class="btn-stock" title="Ajustar Stock"><Icon icon="mdi:package-up" /></button>
              <button @click.stop="abrirModalRecalcularIndividual(producto)" class="btn-calcular" title="Recalcular"><Icon icon="mdi:calculator" /></button>
              <button @click.stop="abrirModalEliminar(producto)" class="btn-eliminar" title="Eliminar"><Icon icon="mdi:delete" /></button>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginación Premium -->
      <div v-if="filteredProductos.length > itemsPerPage" class="bg-gray-50/50 dark:bg-gray-800/50 px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tighter">
          Mostrando {{ startItem }} - {{ Math.min(endItem, filteredProductos.length) }} <span class="mx-1 text-gray-300">|</span> Total: {{ filteredProductos.length }}
        </div>
        <div class="flex items-center gap-1">
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-lg" />
          </button>
          
          <div class="flex items-center gap-1 mx-1">
            <button v-for="page in visiblePages" :key="page" @click="currentPage = page" :class="['w-8 h-8 rounded-lg text-xs font-bold transition-all flex items-center justify-center', currentPage === page ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>

          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL PRODUCTO -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <!-- Overlay -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeModal"></div>

        <!-- Modal Content -->
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden transform transition-all scale-100 animate-fadeIn modal-container">
          <div class="modal-header">
            <div class="modal-header-content">
              <div class="modal-header-icon"><Icon :icon="editingProducto ? 'mdi:pencil-box' : 'mdi:package-variant-plus'" /></div>
              <div><h3>{{ editingProducto ? 'Editar' : 'Nuevo' }}</h3><p>{{ editingProducto ? 'Modifica datos' : 'Registrar producto' }}</p></div>
            </div>
            <button @click="closeModal" class="modal-close"><Icon icon="mdi:close" /></button>
          </div>
          <form @submit.prevent="saveProducto" class="modal-form">
            <div class="modal-body p-0">
              <!-- SECCIÓN 1: INFORMACIÓN BÁSICA -->
              <div class="form-section p-4 border-b border-gray-100 dark:border-gray-800">
                <div class="flex items-center gap-2 mb-3 text-blue-700 dark:text-blue-400">
                  <Icon icon="mdi:information-outline" class="text-xl" />
                  <h4 class="font-bold text-sm uppercase tracking-wider">Información Básica</h4>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="form-group">
                    <label class="flex items-center gap-1.5"><Icon icon="mdi:barcode" class="text-xs" /> Código *</label>
                    <input v-model="form.codigo" type="text" required maxlength="50" placeholder="Ej: PROD-001" @blur="verificarCodigoUnico" class="modern-input" />
                    <p v-if="errors.codigo" class="error-text text-[10px] mt-1">{{ errors.codigo }}</p>
                  </div>
                  <div class="form-group">
                    <label class="flex items-center gap-1.5"><Icon icon="mdi:barcode-scan" class="text-xs" /> Código de Barras</label>
                    <input v-model="form.codigoBarras" type="text" maxlength="100" placeholder="Código de barras (opcional)" @blur="verificarCodigoBarrasUnico" class="modern-input" :class="{ 'border-green-500 bg-green-50 dark:bg-green-900/20': codigoDesdeLector }" />
                    <p v-if="errors.codigoBarras" class="error-text text-[10px] mt-1">{{ errors.codigoBarras }}</p>
                    <p v-if="codigoDesdeLector" class="text-[10px] text-green-600 dark:text-green-400 mt-1">
                      <Icon icon="mdi:check-circle" class="inline" /> Código escaneado automáticamente
                    </p>
                  </div>
                  <div class="form-group">
                    <label class="flex items-center gap-1.5"><Icon icon="mdi:format-title" class="text-xs" /> Nombre *</label>
                    <input v-model="form.nombre" type="text" required maxlength="100" placeholder="Ej: Monitor LED" class="modern-input" @blur="form.nombre = toTitleCase(form.nombre)" />
                  </div>
                  <div class="form-group">
                    <label class="flex items-center gap-1.5"><Icon icon="mdi:folder-outline" class="text-xs" /> Categoría *</label>
                    <select v-model="form.idCategoria" required class="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm">
                      <option value="" class="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">Seleccionar...</option>
                      <option v-for="categoria in categorias" :key="categoria.idCategoria || categoria.IdCategoria" :value="categoria.idCategoria || categoria.IdCategoria" class="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">{{ categoria.nombre || categoria.Nombre || 'Sin nombre' }}</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label class="flex items-center gap-1.5"><Icon icon="mdi:truck-outline" class="text-xs" /> Proveedor *</label>
                    <select v-model="form.idProveedor" required class="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm">
                      <option value="" class="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">Seleccionar...</option>
                      <option v-for="proveedor in proveedores" :key="proveedor.idProveedor || proveedor.IdProveedor" :value="proveedor.idProveedor || proveedor.IdProveedor" class="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">{{ proveedor.nombre || proveedor.Nombre || 'Sin nombre' }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- SECCIÓN 2: DETALLES Y DISEÑO -->
              <div class="form-section p-4 border-b border-gray-100 dark:border-gray-800">
                <div class="flex items-center gap-2 mb-3 text-purple-700 dark:text-purple-400">
                  <Icon icon="mdi:details" class="text-xl" />
                  <h4 class="font-bold text-sm uppercase tracking-wider">Detalles y Apariencia</h4>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div class="space-y-3">
                    <div class="grid grid-cols-2 gap-3">
                      <div class="form-group">
                        <label>Marca</label>
                        <input v-model="form.marca" type="text" maxlength="50" placeholder="Ej: Samsung" class="modern-input" @blur="form.marca = toTitleCase(form.marca)" />
                      </div>
                      <div class="form-group">
                        <label>Modelo</label>
                        <input v-model="form.modelo" type="text" maxlength="50" placeholder="Ej: S24F350" class="modern-input" @blur="form.modelo = toTitleCase(form.modelo)" />
                      </div>
                    </div>
                    <div class="form-group">
                      <label>Descripción</label>
                      <textarea v-model="form.descripcion" rows="3" maxlength="500" placeholder="Breve descripción del producto..." class="modern-input resize-none"></textarea>
                    </div>
                  </div>
                  
                  <div class="form-group">
                    <label>Imagen del Producto</label>
                    <div class="premium-image-upload mt-1">
                      <div class="preview-container">
                        <img v-if="form.imagenPreview || getImageUrl(form.imagen)" :src="form.imagenPreview || getImageUrl(form.imagen)" @error="handleImageError" />
                        <div v-else class="empty-preview">
                          <Icon icon="mdi:image-plus" class="text-3xl opacity-20" />
                          <span class="text-[10px] opacity-40">Sin Imagen</span>
                        </div>
                      </div>
                      <div class="controls">
                        <label class="upload-btn-label">
                          <Icon icon="mdi:camera" />
                          <span>{{ form.imagenPreview || form.imagen ? 'Cambiar' : 'Subir' }}</span>
                          <input type="file" accept="image/*" @change="onImageSelected" hidden />
                        </label>
                        <button v-if="form.imagenPreview || form.imagen" type="button" @click="eliminarImagen" class="remove-btn">
                          <Icon icon="mdi:delete-outline" />
                        </button>
                      </div>
                    </div>
                    <div v-if="imageUploading" class="mt-2">
                      <div class="flex items-center justify-between text-[10px] mb-1">
                        <span>Subiendo...</span>
                        <span>{{ uploadProgress }}%</span>
                      </div>
                      <div class="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div class="h-full bg-blue-500 transition-all duration-300" :style="{ width: uploadProgress + '%' }"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- SECCIÓN 3: PRECIOS Y RENTABILIDAD -->
              <div class="form-section p-4 border-b border-gray-100 dark:border-gray-800">
                <div class="flex items-center gap-2 mb-3 text-green-700 dark:text-green-400">
                  <Icon icon="mdi:cash-multiple" class="text-xl" />
                  <h4 class="font-bold text-sm uppercase tracking-wider">Precios y Rentabilidad</h4>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div class="form-group">
                    <label class="text-green-800 dark:text-green-300 font-semibold">Costo ($) *</label>
                    <input v-model.number="form.precioCompra" type="number" step="0.01" min="0.01" required placeholder="0.00" @input="calcularPrecioVentaBackend" class="modern-input border-green-200 focus:border-green-500" />
                  </div>
                  <div class="form-group">
                    <label>Ganancia por</label>
                    <select v-model="form.tipoGanancia" @change="calcularPrecioVentaBackend" class="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm">
                      <option value="PORCENTAJE" class="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">Porcentaje (%)</option>
                      <option value="MONTO_FIJO" class="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">Monto Fijo ($)</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>Valor Ganancia</label>
                    <input v-model.number="form.valorGanancia" type="number" step="0.01" min="0" :placeholder="form.tipoGanancia === 'PORCENTAJE' ? '30' : '15'" @input="calcularPrecioVentaBackend" class="modern-input" />
                  </div>
                  <div class="form-group">
                    <label>Precio Especial ($)</label>
                    <input v-model.number="form.precioEspecialUsd" type="number" step="0.01" min="0" placeholder="0.00" class="modern-input" />
                  </div>
                  <div class="form-group sm:col-span-2">
                    <label class="text-blue-600 dark:text-blue-400 font-bold italic">Precio de Venta Sugerido (Calculado)</label>
                    <div class="relative">
                      <input :value="form.precioVentaCalculado" type="text" readonly class="modern-input bg-blue-100/50 dark:bg-blue-900/40 font-bold text-blue-700 dark:text-blue-300 border-dashed border-blue-400" />
                      <Icon icon="mdi:auto-fix" class="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- SECCIÓN 4: INVENTARIO Y GARANTÍA -->
              <div class="form-section p-4">
                <div class="flex items-center gap-2 mb-3 text-orange-700 dark:text-orange-400">
                  <Icon icon="mdi:store-24-hour" class="text-xl" />
                  <h4 class="font-bold text-sm uppercase tracking-wider">Inventario y Garantía</h4>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div class="form-group">
                    <label>Stock Actual</label>
                    <input v-model.number="form.stock" type="number" min="0" placeholder="0" class="modern-input" />
                  </div>
                  <div class="form-group">
                    <label>Stock Mín.</label>
                    <input v-model.number="form.stockMinimo" type="number" min="0" placeholder="5" class="modern-input text-red-500" />
                  </div>
                  <div class="form-group">
                    <label>Stock Máx.</label>
                    <input v-model.number="form.stockMaximo" type="number" min="0" placeholder="Opcional" class="modern-input" />
                  </div>
                  <div class="form-group">
                    <label>Meses Garantía</label>
                    <input v-model.number="form.garantiaMeses" type="number" min="0" placeholder="0" class="modern-input" />
                  </div>
                </div>
                <div class="flex items-center justify-between mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-dashed border-gray-300 dark:border-gray-600">
                  <label class="text-xs font-bold text-gray-500 uppercase">Estado del Producto:</label>
                  <div class="flex bg-white dark:bg-gray-800 p-1 rounded-lg shadow-inner gap-1">
                    <button type="button" @click="form.estado = 'Activo'" :class="['px-4 py-1.5 rounded-md text-[10px] font-bold uppercase transition-all', form.estado === 'Activo' ? 'bg-green-500 text-white shadow-md' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700']">Activo</button>
                    <button type="button" @click="form.estado = 'Inactivo'" :class="['px-4 py-1.5 rounded-md text-[10px] font-bold uppercase transition-all', form.estado === 'Inactivo' ? 'bg-red-500 text-white shadow-md' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700']">Inactivo</button>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" @click="closeModal" class="btn-cancelar">Cancelar</button>
              <button type="submit" :disabled="saving || !isFormValid" class="btn-guardar">
                <Icon v-if="saving" icon="mdi:loading" class="spinning" />
                {{ saving ? 'Guardando...' : (editingProducto ? 'Actualizar' : 'Crear') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- MODAL STOCK -->
    <Teleport to="body">
      <div v-if="showStockModal" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <!-- Overlay -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="closeStockModal"></div>

        <!-- Modal Content -->
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden transform transition-all scale-100 animate-fadeIn modal-stock">
          <div class="modal-header-stock">
            <div><Icon icon="mdi:package-up" /><h3>Ajustar Stock</h3><p>{{ stockProducto?.nombre }}</p></div>
            <button @click="closeStockModal"><Icon icon="mdi:close" /></button>
          </div>
          <form @submit.prevent="saveStock">
            <div class="stock-actual">Stock actual: <strong>{{ stockProducto?.stock || 0 }}</strong></div>
            <div class="form-group"><label>Operación</label><select v-model="stockForm.operacion"><option value="incrementar">Incrementar</option><option value="disminuir">Disminuir</option></select></div>
            <div class="form-group"><label>Cantidad</label><input v-model.number="stockForm.cantidad" type="number" min="0" required placeholder="Cantidad" /></div>
            <div class="form-group"><label>Motivo</label><input v-model="stockForm.motivo" type="text" placeholder="Opcional" /></div>
            <div class="modal-footer"><button type="button" @click="closeStockModal">Cancelar</button><button type="submit" :disabled="savingStock">{{ savingStock ? 'Guardando...' : 'Aplicar' }}</button></div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- MODAL CONFIRMACIÓN -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <!-- Overlay -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="cancelarConfirmacion"></div>

        <!-- Modal Content -->
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden transform transition-all scale-100 animate-fadeIn modal-confirm" :class="confirmModalColor">
          <div class="confirm-header"><Icon :icon="confirmModalIcon" /><div><h3>{{ confirmModalTitulo }}</h3><p>{{ confirmModalSubtitulo }}</p></div></div>
          <div class="confirm-body"><p>{{ confirmModalMensaje }}</p><p v-if="confirmModalDetalle" class="confirm-detalle">{{ confirmModalDetalle }}</p></div>
          <div class="confirm-footer"><button @click="cancelarConfirmacion">Cancelar</button><button @click="ejecutarAccionConfirmada" :class="confirmModalButtonColor">{{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}</button></div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";
import {
  obtenerProductos, 
  crearProducto, 
  actualizarProducto, 
  eliminarProducto,
  cambiarEstadoProducto, 
  actualizarStock, 
  subirImagenProducto, 
  eliminarImagenProducto,
  verificarCodigo,
  verificarCodigoBarras,
  ordenarProductos, 
  formatearMonto, 
  obtenerUrlImagen,
  obtenerProductoPorCodigo,
  obtenerProductoPorCodigoBarras
} from '@/api/productos.js';
import { obtenerCategorias } from '@/api/categorias.js';
import { obtenerProveedores } from '@/api/proveedores.js';
import { obtenerTasaCompraActual } from '@/api/tasaCompra.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { filtrarProductosParaReporte, cargarLogo } from '@/utils/reporteProductosGenerator.js';
import { toTitleCase } from '@/utils/stringUtils.js';
import { notificarProducto } from '@/utils/notificacionesHelper';

export default {
  name: 'ProductosList',
  components: { Icon },
  setup() { const toast = useToast(); return { toast }; },
  data() {
    return {
      serverConfig: { host: '192.168.9.195', port: '5090', localhost: 'localhost' },
      productos: [], categorias: [], proveedores: [],
      tasaUSDT: null, tasaBCV: null, tasaInterval: null,
      loading: false, saving: false, savingStock: false,
      showModal: false, showStockModal: false,
      searchQuery: '', filterStatus: 'all', filterStock: 'all', filterProveedor: 'all', sortBy: 'nombre',
      currentPage: 1, itemsPerPage: 6,
      // Lector de código
      codigoEscaneado: '',
      productoEncontrado: null,
      busquedaFallida: false,
      buscando: false,
      codigoDesdeLector: false,
      form: {
        idProducto: null, 
        codigo: '', 
        codigoBarras: '',
        nombre: '', 
        descripcion: '', 
        marca: '', 
        modelo: '',
        precioCompra: '', 
        precioVentaCalculado: '0.00', 
        precioVentaEnviar: null, 
        precioEspecialUsd: null,
        stock: 0, 
        stockMinimo: 5, 
        stockMaximo: null, 
        garantiaMeses: 0, 
        garantiaDescripcion: '',
        idCategoria: '', 
        idProveedor: '', 
        imagen: '', 
        imagenPreview: '', 
        estado: 'Activo',
        tipoGanancia: 'PORCENTAJE', 
        valorGanancia: 30, 
        ubicacion: '', 
        imagenFile: null
      },
      stockForm: { operacion: 'incrementar', cantidad: 0, motivo: '' },
      stockProducto: null, errors: {}, editingProducto: null, lastUpdate: new Date(),
      imageUploading: false, uploadProgress: 0,
      showConfirmModal: false, confirmModalTipo: '', confirmModalTitulo: '', confirmModalSubtitulo: '',
      confirmModalMensaje: '', confirmModalDetalle: '', confirmModalIcon: '', confirmModalColor: '',
      confirmModalButtonColor: '', confirmModalBotonTexto: '', confirmModalProducto: null,
      confirmModalCargando: false, confirmModalAccion: null, confirmModalDatos: null,
    };
  },
  computed: {
    serverIp() { return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? this.serverConfig.localhost : this.serverConfig.host; },
    serverPort() { return this.serverConfig.port; },
    imageBaseUrl() { return `http://${this.serverIp}:${this.serverPort}/imagenes/producto`; },
    filteredProductos() {
      let filtered = [...this.productos];
      if (this.searchQuery.trim()) {
        const normalize = (str) => (str || '').toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const terms = normalize(this.searchQuery).split(/\s+/).filter(Boolean);
        
        filtered = filtered.filter(p => {
          const searchableText = normalize([
            p.codigo, p.Codigo,
            p.codigoBarras, p.CodigoBarras,
            p.nombre, p.Nombre,
            p.descripcion, p.Descripcion,
            p.marca, p.Marca,
            p.modelo, p.Modelo,
            p.nombreCategoria, p.NombreCategoria,
            p.nombreProveedor, p.NombreProveedor
          ].join(' '));
          
          return terms.every(term => searchableText.includes(term));
        });
      }
      if (this.filterStatus === 'activos') filtered = filtered.filter(p => p.estado?.toLowerCase() === 'activo');
      else if (this.filterStatus === 'inactivos') filtered = filtered.filter(p => p.estado?.toLowerCase() === 'inactivo');
      if (this.filterStock !== 'all') filtered = filtrarProductosParaReporte(filtered, this.filterStock);
      if (this.filterProveedor !== 'all') filtered = filtered.filter(p => p.idProveedor === parseInt(this.filterProveedor));
      return ordenarProductos(filtered, this.sortBy);
    },
    paginatedProductos() { const start = (this.currentPage - 1) * this.itemsPerPage; return this.filteredProductos.slice(start, start + this.itemsPerPage); },
    totalPages() { return Math.ceil(this.filteredProductos.length / this.itemsPerPage); },
    visiblePages() { const pages = []; const maxVisible = 5; let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2)); let end = Math.min(this.totalPages, start + maxVisible - 1); if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1); for (let i = start; i <= end; i++) pages.push(i); return pages; },
    startItem() { return (this.currentPage - 1) * this.itemsPerPage + 1; },
    endItem() { return Math.min(this.currentPage * this.itemsPerPage, this.filteredProductos.length); },
    isFormValid() { return this.form.codigo.trim() && this.form.nombre.trim() && this.form.precioCompra && this.form.precioCompra > 0 && this.form.idCategoria && this.form.idProveedor && this.tasaUSDT && this.tasaBCV; },
  },
  watch: {
    'form.precioCompra'() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    'form.tipoGanancia'() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    'form.valorGanancia'() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    tasaUSDT() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    tasaBCV() { if (this.showModal) this.calcularPrecioVentaBackend(); },
    filterStatus() { this.currentPage = 1; },
    filterStock() { this.currentPage = 1; },
    filterProveedor() { this.currentPage = 1; },
    searchQuery() { this.currentPage = 1; },
    sortBy() { this.currentPage = 1; },
  },
  async mounted() { 
    await cargarLogo(); 
    await this.cargarTasasCompletas(); 
    await this.loadData(); 
    this.tasaInterval = setInterval(() => this.cargarTasasCompletas(), 5 * 60 * 1000);
    this.setupLectorAutomatico();
    this.$nextTick(() => {
      this.$refs.lectorInput?.focus();
    });
  },
  beforeUnmount() { 
    if (this.tasaInterval) clearInterval(this.tasaInterval); 
  },
  methods: {
    toTitleCase,
    // ============ LECTOR DE CÓDIGO ============
    setupLectorAutomatico() {
      let codigoTemporal = '';
      let ultimoTiempo = 0;
      const TIEMPO_LECTOR = 50;
      const TIEMPO_RESET = 200;

      document.addEventListener('keydown', (e) => {
        const ahora = Date.now();
        const diferencia = ahora - ultimoTiempo;
        ultimoTiempo = ahora;

        if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || 
            e.key === 'CapsLock' || e.key === 'Tab' || e.key === 'Escape') {
          return;
        }

        if (diferencia < TIEMPO_LECTOR && e.key !== 'Enter') {
          codigoTemporal += e.key;
          e.preventDefault();
        }

        if (e.key === 'Enter' && codigoTemporal.length > 2) {
          e.preventDefault();
          this.codigoEscaneado = codigoTemporal;
          this.buscarProductoPorCodigo();
          codigoTemporal = '';
        }

        if (diferencia > TIEMPO_RESET && e.key !== 'Enter') {
          codigoTemporal = '';
        }
      });
    },
    async buscarProductoPorCodigo() {
      const codigo = this.codigoEscaneado.trim();
      if (!codigo) {
        this.toast.warning('Ingresa o escanea un código');
        return;
      }

      let producto = this.productos.find(p => {
        const codigoEsc = codigo.toLowerCase();
        return p.codigo?.toLowerCase() === codigoEsc ||
               p.codigoBarras?.toLowerCase() === codigoEsc;
      });

      if (!producto) {
        this.buscando = true;
        try {
          let resultado = await obtenerProductoPorCodigo(codigo);
          
          if (!resultado.success || !resultado.data) {
            resultado = await obtenerProductoPorCodigoBarras(codigo);
          }
          
          if (resultado.success && resultado.data) {
            producto = resultado.data;
            const existe = this.productos.some(p => p.idProducto === producto.idProducto);
            if (!existe) {
              this.productos.push(producto);
            }
          }
        } catch (error) {
          console.error('Error buscando producto:', error);
          this.toast.error('Error al buscar el producto');
          this.buscando = false;
          return;
        }
        this.buscando = false;
      }

      if (producto) {
        this.productoEncontrado = producto;
        this.busquedaFallida = false;
        this.codigoEscaneado = '';
        this.$refs.lectorInput?.focus();
        this.toast.success(`Producto encontrado: ${producto.nombre}`);
      } else {
        this.productoEncontrado = null;
        this.busquedaFallida = true;
        this.toast.error('Producto no encontrado');
      }
    },
    limpiarBusqueda() {
      this.codigoEscaneado = '';
      this.productoEncontrado = null;
      this.busquedaFallida = false;
      this.buscando = false;
      this.codigoDesdeLector = false;
      this.$refs.lectorInput?.focus();
    },
    abrirCrearDesdeCodigoEscaneado() {
      const codigoEscaneado = this.codigoEscaneado.trim();
      this.openCreateModal();
      
      if (codigoEscaneado) {
        this.form.codigoBarras = codigoEscaneado;
        this.codigoDesdeLector = true;
        this.toast.info('Código de barras cargado desde el escáner');
      }
      
      this.limpiarBusqueda();
    },
    copiarProducto(producto) {
      this.openCreateModal();
      this.form.codigo = `${producto.codigo}-COPIA`;
      this.form.codigoBarras = producto.codigoBarras || '';
      this.form.nombre = `${producto.nombre} (Copia)`;
      this.form.marca = producto.marca;
      this.form.modelo = producto.modelo;
      this.form.descripcion = producto.descripcion;
      this.form.precioCompra = producto.precio_compra_usdt;
      this.form.idCategoria = producto.idCategoria;
      this.form.idProveedor = producto.idProveedor;
      this.form.tipoGanancia = producto.tipo_ganancia || 'PORCENTAJE';
      this.form.valorGanancia = producto.valor_ganancia || 30;
      this.codigoDesdeLector = false;
      this.toast.success('Producto copiado, edita y guarda');
    },
    // ============ FIN LECTOR DE CÓDIGO ============

    // ============ CÁLCULO DE PRECIOS CORREGIDO ============
    /**
     * Calcula el precio de venta en USD.
     * ✅ REGLA: El costo real es en USD (USDT). El BCV NO entra en el cálculo del costo.
     * ✅ El BCV solo se usa al final para convertir USD → Bs.
     */
    calcularPrecioVentaBackend() {
      if (!this.tasaUSDT || !this.tasaBCV || !this.form.precioCompra || this.form.precioCompra <= 0) { 
        this.form.precioVentaCalculado = '0.00'; 
        this.form.precioVentaEnviar = null; 
        return; 
      }
      
      // ✅ El costo REAL es en USD (USDT). El BCV NO entra aquí.
      const costoRealUSD = parseFloat(this.form.precioCompra);
      
      // ✅ Precio de venta en USD = costo + margen
      let precioVentaUSD;
      if (this.form.tipoGanancia === 'PORCENTAJE') { 
        precioVentaUSD = costoRealUSD * (1 + ((this.form.valorGanancia || 30) / 100)); 
      } else { 
        precioVentaUSD = costoRealUSD + (this.form.valorGanancia || 15); 
      }
      
      const precioVentaRedondeado = Math.round(precioVentaUSD * 100) / 100;
      this.form.precioVentaCalculado = precioVentaRedondeado.toFixed(2);
      this.form.precioVentaEnviar = precioVentaRedondeado;
      return precioVentaRedondeado;
    },
    // ============ FIN CÁLCULO DE PRECIOS ============

    calcularMargenBolivares(producto) { 
      if (!this.tasaUSDT || !this.tasaBCV || !producto.precio_compra_usdt || !producto.precio_venta_usd) return 0; 
      const tasaCompra = this.tasaUSDT?.precio_usdt_bs || 1; 
      const tasaVenta = this.tasaBCV?.precio_usd_bs || 1; 
      return (producto.precio_venta_usd * tasaVenta) - (producto.precio_compra_usdt * tasaCompra); 
    },
    calcularDiferencia(precioCompra, precioVenta) { 
      return parseFloat(precioVenta || 0) - parseFloat(precioCompra || 0); 
    },
    calcularPorcentajeGanancia(precioCompra, precioVenta) { 
      const ganancia = this.calcularDiferencia(precioCompra, precioVenta); 
      if (!ganancia || ganancia <= 0 || !precioCompra || precioCompra <= 0) return '0'; 
      return ((ganancia / precioCompra) * 100).toFixed(2); 
    },
    formatBolivares(montoUSD, tipo = 'venta') { 
      if (!montoUSD || isNaN(montoUSD)) return '0 Bs.'; 
      let bolivares = 0; 
      if (tipo === 'compra' && this.tasaUSDT) bolivares = parseFloat(montoUSD) * (this.tasaUSDT?.precio_usdt_bs || 1); 
      else if (tipo === 'venta' && this.tasaBCV) bolivares = parseFloat(montoUSD) * (this.tasaBCV?.precio_usd_bs || 1); 
      else bolivares = parseFloat(montoUSD) * (this.tasaUSDT?.precio_usdt_bs || this.tasaBCV?.precio_usd_bs || 1); 
      return this.formatNumber(bolivares, 0) + ' Bs.'; 
    },
    formatNumber(numero, decimales = 2) { return formatearMonto(numero, decimales); },
    formatDate(fechaISO, completo = true) { 
      if (!fechaISO) return ''; 
      try { 
        const fecha = new Date(fechaISO); 
        const opciones = { year: 'numeric', month: '2-digit', day: '2-digit' }; 
        if (completo) opciones.hour = '2-digit', opciones.minute = '2-digit'; 
        return fecha.toLocaleDateString('es-VE', opciones); 
      } catch (error) { return fechaISO; } 
    },
    formatTime(fechaISO) { 
      if (!fechaISO) return ''; 
      try { 
        const fecha = new Date(fechaISO); 
        return fecha.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', hour12: false }); 
      } catch (error) { return ''; } 
    },
    getStockClass(stock, stockMinimo) { 
      const stockNum = Number(stock) || 0; 
      const minNum = Number(stockMinimo) || 0; 
      if (stockNum <= 0) return 'stock-cero'; 
      if (minNum > 0 && stockNum <= minNum) return 'stock-bajo'; 
      return 'stock-normal'; 
    },
    async onImageSelected(event) { 
      const file = event.target.files[0]; 
      if (!file) return; 
      const tipos = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']; 
      if (!tipos.includes(file.type)) { this.toast.error('Formato no válido'); return; } 
      if (file.size > 5 * 1024 * 1024) { this.toast.error('Máx. 5MB'); return; } 
      this.form.imagenFile = file; 
      const reader = new FileReader(); 
      reader.onload = (e) => { this.form.imagenPreview = e.target.result; }; 
      reader.readAsDataURL(file); 
    },
    async subirImagenDespuesDeGuardar(productoId) { 
      if (!this.form.imagenFile) return; 
      this.imageUploading = true; 
      this.uploadProgress = 0; 
      try { 
        const interval = setInterval(() => { if (this.uploadProgress < 90) this.uploadProgress += 10; }, 100); 
        const res = await subirImagenProducto(this.form.imagenFile, productoId); 
        clearInterval(interval); 
        this.uploadProgress = 100; 
        if (res.success) { 
          this.form.imagen = res.data.nombreArchivo; 
          this.toast.success('Imagen subida'); 
          await actualizarProducto(productoId, { imagen: res.data.nombreArchivo }); 
        } else { this.toast.error(res.error || 'Error'); } 
      } catch (error) { this.toast.error('Error'); } 
      finally { this.imageUploading = false; this.uploadProgress = 0; this.form.imagenFile = null; } 
    },
    async eliminarImagen() { 
      if (!confirm('¿Eliminar esta imagen?')) return; 
      this.imageUploading = true; 
      try { 
        if (this.editingProducto?.idProducto) { 
          const res = await eliminarImagenProducto('producto', this.editingProducto.idProducto); 
          if (res.success) { 
            this.form.imagen = ''; 
            this.form.imagenPreview = ''; 
            this.toast.success('Imagen eliminada'); 
          } else { this.toast.error(res.error || 'Error'); } 
        } else { 
          this.form.imagen = ''; 
          this.form.imagenPreview = ''; 
          this.form.imagenFile = null; 
        } 
      } catch (error) { this.toast.error('Error'); } 
      finally { this.imageUploading = false; } 
    },
    getImageUrl(imagen) { 
      return obtenerUrlImagen(imagen);
    },
    handleImageError(event) { event.target.style.display = 'none'; },
    abrirModalConfirmacion(config) { 
      this.confirmModalTipo = config.tipo; 
      this.confirmModalTitulo = config.titulo; 
      this.confirmModalSubtitulo = config.subtitulo || ''; 
      this.confirmModalMensaje = config.mensaje; 
      this.confirmModalDetalle = config.detalle || ''; 
      this.confirmModalIcon = config.icon || 'mdi:help-circle'; 
      this.confirmModalColor = config.color || ''; 
      this.confirmModalButtonColor = config.buttonColor || ''; 
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar'; 
      this.confirmModalProducto = config.producto || null; 
      this.confirmModalAccion = config.accion; 
      this.confirmModalDatos = config.datos || null; 
      this.showConfirmModal = true; 
    },
    cancelarConfirmacion() { 
      this.showConfirmModal = false; 
      this.confirmModalCargando = false; 
      this.confirmModalProducto = null; 
      this.confirmModalAccion = null; 
      this.confirmModalDatos = null; 
    },
    async ejecutarAccionConfirmada() { 
      if (this.confirmModalAccion) { 
        this.confirmModalCargando = true; 
        await this.confirmModalAccion(this.confirmModalDatos); 
        this.confirmModalCargando = false; 
        this.cancelarConfirmacion(); 
      } 
    },
    abrirModalEliminar(producto) { 
      this.abrirModalConfirmacion({ 
        tipo: 'eliminar', titulo: 'Eliminar', subtitulo: 'Esta acción no se puede deshacer', 
        mensaje: `¿Eliminar "${producto.nombre}"?`, detalle: 'Eliminación permanente.', 
        icon: 'mdi:alert-circle', color: 'confirm-danger', buttonColor: 'btn-danger', 
        botonTexto: 'Eliminar', producto: producto, accion: () => this.deleteProducto(producto.idProducto) 
      }); 
    },
    abrirModalToggleStatus(producto) { 
      const esActivo = producto.estado?.toLowerCase() === 'activo'; 
      this.abrirModalConfirmacion({ 
        tipo: 'toggle-status', titulo: esActivo ? 'Desactivar' : 'Activar', subtitulo: 'Cambiar estado', 
        mensaje: `¿${esActivo ? 'desactivar' : 'activar'} "${producto.nombre}"?`, 
        detalle: `Pasará a "${esActivo ? 'Inactivo' : 'Activo'}"`, 
        icon: esActivo ? 'mdi:close-circle' : 'mdi:check-circle', 
        color: esActivo ? 'confirm-warning' : 'confirm-success', 
        buttonColor: esActivo ? 'btn-warning' : 'btn-success', 
        botonTexto: esActivo ? 'Desactivar' : 'Activar', producto: producto, accion: () => this.toggleStatus(producto) 
      }); 
    },
    abrirModalRecalcularMasivo() { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.toast.error('No hay tasas'); return; } 
      this.abrirModalConfirmacion({ 
        tipo: 'recalcular-masivo', titulo: 'Recalcular Precios', subtitulo: 'Actualizar todos', 
        mensaje: `¿Recalcular ${this.filteredProductos.length} productos?`, 
        detalle: 'Se usarán tasas actuales.', icon: 'mdi:calculator', color: 'confirm-amber', 
        buttonColor: 'btn-amber', botonTexto: 'Recalcular', accion: this.recalcularPreciosMasivos 
      }); 
    },
    abrirModalRecalcularIndividual(producto) { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.toast.error('No hay tasas'); return; } 
      this.abrirModalConfirmacion({ 
        tipo: 'recalcular-individual', titulo: 'Recalcular Precio', subtitulo: 'Actualizar precio', 
        mensaje: `¿Recalcular "${producto.nombre}"?`, detalle: 'Se usará tasa actual.', 
        icon: 'mdi:calculator', color: 'confirm-blue', buttonColor: 'btn-blue', 
        botonTexto: 'Recalcular', producto: producto, accion: () => this.recalcularPrecioIndividual(producto) 
      }); 
    },
    mostrarExito(mensaje) { this.toast.success(mensaje, { timeout: 3000 }); },
    mostrarError(mensaje) { this.toast.error(mensaje, { timeout: 5000 }); },
    mostrarAdvertencia(mensaje) { this.toast.warning(mensaje, { timeout: 4000 }); },
    async cargarTasasCompletas() { 
      try { 
        const compra = await obtenerTasaCompraActual(); 
        if (compra.success) this.tasaUSDT = compra.data; 
        const venta = await obtenerTasaVentaActual(); 
        if (venta.success) this.tasaBCV = venta.data; 
        if (this.showModal) this.calcularPrecioVentaBackend(); 
      } catch (error) { this.mostrarError('Error al cargar tasas'); } 
    },
    async loadData() { 
      this.loading = true; 
      try { 
        const [prod, cat, prov] = await Promise.all([obtenerProductos(this.filterStatus), obtenerCategorias('activas'), obtenerProveedores('activos')]); 
        if (prod.success) { this.productos = prod.data || []; this.lastUpdate = new Date(); } 
        if (cat.success) this.categorias = cat.data || []; 
        if (prov.success) this.proveedores = prov.data || []; 
      } catch (error) { this.mostrarError('Error al cargar datos'); } 
      finally { this.loading = false; } 
    },
    handleFilterChange() { this.currentPage = 1; this.loadData(); },
    handleSearch: debounce(function() { this.currentPage = 1; }, 300),
    openCreateModal() { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.mostrarError('No hay tasas'); return; } 
      this.editingProducto = null; 
      this.resetForm(); 
      this.errors = {}; 
      this.codigoDesdeLector = false;
      this.showModal = true; 
      this.$nextTick(() => { if (this.form.precioCompra) this.calcularPrecioVentaBackend(); }); 
    },
    openEditModal(producto) { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.mostrarError('No hay tasas'); return; } 
      this.editingProducto = producto; 
      this.form = { 
        idProducto: producto.idProducto, 
        codigo: producto.codigo || '', 
        codigoBarras: producto.codigoBarras || '',
        nombre: producto.nombre || '', 
        descripcion: producto.descripcion || '', 
        marca: producto.marca || '', 
        modelo: producto.modelo || '', 
        precioCompra: producto.precio_compra_usdt || '', 
        precioVentaCalculado: producto.precio_venta_usd ? parseFloat(producto.precio_venta_usd).toFixed(2) : '0.00', 
        precioVentaEnviar: producto.precio_venta_usd || null, 
        precioEspecialUsd: producto.precio_especial_usd || null, 
        stock: producto.stock || 0, 
        stockMinimo: producto.stock_minimo || 5, 
        stockMaximo: producto.stockMaximo || null, 
        garantiaMeses: producto.garantia_meses || 0, 
        garantiaDescripcion: producto.garantia_descripcion || '', 
        idCategoria: producto.idCategoria || '', 
        idProveedor: producto.idProveedor || '', 
        imagen: producto.imagen || '', 
        imagenPreview: '', 
        estado: producto.estado || 'Activo', 
        tipoGanancia: producto.tipo_ganancia || 'PORCENTAJE', 
        valorGanancia: producto.valor_ganancia || 30, 
        ubicacion: producto.ubicacion || '', 
        imagenFile: null 
      }; 
      this.errors = {}; 
      this.codigoDesdeLector = false;
      this.showModal = true; 
      this.$nextTick(() => { this.calcularPrecioVentaBackend(); }); 
    },
    openStockModal(producto) { 
      this.stockProducto = producto; 
      this.stockForm = { operacion: 'incrementar', cantidad: 0, motivo: '' }; 
      this.showStockModal = true; 
    },
    closeModal() { this.showModal = false; this.resetForm(); this.errors = {}; this.codigoDesdeLector = false; },
    closeStockModal() { this.showStockModal = false; this.stockProducto = null; this.stockForm = { operacion: 'incrementar', cantidad: 0, motivo: '' }; },
    resetForm() { 
      if (this.form.imagenPreview) URL.revokeObjectURL(this.form.imagenPreview); 
      this.form = { 
        idProducto: null, 
        codigo: '', 
        codigoBarras: '',
        nombre: '', 
        descripcion: '', 
        marca: '', 
        modelo: '', 
        precioCompra: '', 
        precioVentaCalculado: '0.00', 
        precioVentaEnviar: null, 
        precioEspecialUsd: null, 
        stock: 0, 
        stockMinimo: 5, 
        stockMaximo: null, 
        garantiaMeses: 0, 
        garantiaDescripcion: '', 
        idCategoria: '', 
        idProveedor: '', 
        imagen: '', 
        imagenPreview: '', 
        estado: 'Activo', 
        tipoGanancia: 'PORCENTAJE', 
        valorGanancia: 30, 
        ubicacion: '', 
        imagenFile: null 
      }; 
      this.codigoDesdeLector = false;
      this.editingProducto = null; 
    },
    async verificarCodigoUnico() { 
      if (!this.form.codigo.trim() || this.editingProducto?.codigo === this.form.codigo) return; 
      try { 
        const res = await verificarCodigo(this.form.codigo.trim(), this.editingProducto?.idProducto); 
        if (res.success && res.existe) this.errors.codigo = 'El código ya existe'; 
        else delete this.errors.codigo; 
      } catch (error) { } 
    },
    async verificarCodigoBarrasUnico() { 
      if (!this.form.codigoBarras?.trim()) {
        delete this.errors.codigoBarras;
        return;
      }
      if (this.editingProducto?.codigoBarras === this.form.codigoBarras) {
        delete this.errors.codigoBarras;
        return;
      }
      try { 
        const res = await verificarCodigoBarras(this.form.codigoBarras.trim(), this.editingProducto?.idProducto); 
        if (res.success && res.existe) this.errors.codigoBarras = 'El código de barras ya existe'; 
        else delete this.errors.codigoBarras; 
      } catch (error) { 
        console.error('Error verificando código de barras:', error);
      } 
    },
    validateForm() { 
      this.errors = {}; 
      if (!this.form.codigo.trim()) this.errors.codigo = 'Código requerido'; 
      if (!this.form.nombre.trim()) this.errors.nombre = 'Nombre requerido'; 
      if (!this.form.precioCompra || parseFloat(this.form.precioCompra) <= 0) this.errors.precioCompra = 'Precio > 0'; 
      if (!this.form.idCategoria) this.errors.idCategoria = 'Categoría requerida'; 
      if (!this.form.idProveedor) this.errors.idProveedor = 'Proveedor requerido'; 
      return Object.keys(this.errors).length === 0; 
    },
    async saveProducto() { 
      if (!this.validateForm()) return; 
      if (!this.tasaUSDT || !this.tasaBCV) { this.mostrarError('No hay tasas'); return; } 
      this.saving = true; 
      try { 
        const precioVenta = this.calcularPrecioVentaBackend(); 
        if (!precioVenta || precioVenta <= 0) throw new Error('Error calculando precio'); 
        const data = { 
          codigo: this.form.codigo.trim(), 
          codigoBarras: this.form.codigoBarras?.trim() || null,
          nombre: this.form.nombre.trim(), 
          descripcion: this.form.descripcion?.trim() || null, 
          marca: this.form.marca?.trim() || null, 
          modelo: this.form.modelo?.trim() || null, 
          precio_compra_usdt: parseFloat(this.form.precioCompra), 
          tipo_ganancia: this.form.tipoGanancia, 
          valor_ganancia: parseFloat(this.form.valorGanancia || (this.form.tipoGanancia === 'PORCENTAJE' ? 30 : 15)), 
          precio_venta_usd: precioVenta, 
          precio_venta_bs: precioVenta * (this.tasaBCV?.precio_usd_bs || 1), 
          precio_especial_usd: this.form.precioEspecialUsd ? parseFloat(this.form.precioEspecialUsd) : null, 
          stock: this.form.stock || 0, 
          stock_minimo: this.form.stockMinimo || 5, 
          stockMaximo: this.form.stockMaximo || null, 
          garantia_meses: this.form.garantiaMeses || 0, 
          garantia_descripcion: this.form.garantiaDescripcion || null, 
          imagen: this.editingProducto ? (this.form.imagen || null) : null, 
          ubicacion: this.form.ubicacion || null, 
          estado: this.form.estado, 
          idCategoria: parseInt(this.form.idCategoria), 
          idProveedor: parseInt(this.form.idProveedor) 
        }; 
        let result, productoId; 
        if (this.editingProducto) { 
          result = await actualizarProducto(this.editingProducto.idProducto, data); 
          productoId = this.editingProducto.idProducto; 
          if (result.success && this.form.imagenFile) await this.subirImagenDespuesDeGuardar(productoId); 
        } else { 
          result = await crearProducto(data); 
          productoId = result.data?.idProducto; 
          if (result.success && this.form.imagenFile && productoId) await this.subirImagenDespuesDeGuardar(productoId); 
        } 
        if (result.success) { 
          await this.loadData(); 
          this.closeModal(); 
          this.codigoDesdeLector = false;
          this.mostrarExito(this.editingProducto ? 'Actualizado' : 'Creado'); 
          notificarProducto(
            this.editingProducto ? 'Producto actualizado' : 'Nuevo producto registrado',
            this.form.nombre,
            `Código: ${this.form.codigo} · $${parseFloat(this.form.precioCompra).toFixed(2)} USD`
          );
        } else { throw new Error(result.error || 'Error'); } 
      } catch (error) { this.mostrarError(error.message); } 
      finally { this.saving = false; } 
    },
    async deleteProducto(id) { 
      try { 
        const res = await eliminarProducto(id); 
        if (res.success) { await this.loadData(); this.mostrarExito('Eliminado'); notificarProducto('Producto eliminado', `ID: ${id}`, 'Eliminado del inventario'); } 
        else { throw new Error(res.error); } 
      } catch (error) { this.mostrarError(error.message); } 
    },
    async toggleStatus(producto) { 
      try { 
        const nuevoEstado = producto.estado?.toLowerCase() === 'activo' ? 'Inactivo' : 'Activo'; 
        const res = await cambiarEstadoProducto(producto.idProducto, nuevoEstado); 
        if (res.success) { await this.loadData(); this.mostrarExito(`Producto ${nuevoEstado.toLowerCase()}`); } 
        else { throw new Error(res.error); } 
      } catch (error) { this.mostrarError('Error'); } 
    },

    // ============ RECÁLCULO INDIVIDUAL CORREGIDO ============
    /**
     * Recalcula el precio de un producto individual.
     * ✅ REGLA: El costo real es en USD. El BCV NO entra en el costo.
     * ✅ REGLA DE PISO: El precio en USD NUNCA baja del anterior.
     * ✅ El BCV solo se usa para convertir USD → Bs.
     */
    async recalcularPrecioIndividual(producto) { 
      if (!this.tasaUSDT || !this.tasaBCV) { this.mostrarError('No hay tasas'); return; } 
      try { 
        // ✅ Costo REAL en USD (USDT). El BCV NO entra en el costo.
        const costoRealUSD = parseFloat(producto.precio_compra_usdt); 
        
        // ✅ Precio de venta en USD = costo + margen
        let precioVentaUSD;
        if (producto.tipo_ganancia === 'PORCENTAJE') { 
          precioVentaUSD = costoRealUSD * (1 + ((producto.valor_ganancia || 30) / 100)); 
        } else { 
          precioVentaUSD = costoRealUSD + (producto.valor_ganancia || 15); 
        } 
        
        // ✅ REGLA DE PISO: el precio NUNCA baja del anterior
        if (producto.precio_venta_usd > 0 && precioVentaUSD < producto.precio_venta_usd) {
          precioVentaUSD = producto.precio_venta_usd;
        }
        
        const nuevoPrecioUSD = Math.round(precioVentaUSD * 100) / 100; 
        const nuevoPrecioBs = Math.round(nuevoPrecioUSD * (this.tasaBCV?.precio_usd_bs || 1)); 
        
        const res = await actualizarProducto(producto.idProducto, { 
          ...producto, 
          precio_venta_usd: nuevoPrecioUSD, 
          precio_venta_bs: nuevoPrecioBs 
        }); 
        if (res.success) { await this.loadData(); this.mostrarExito('Precio recalculado'); } 
        else { throw new Error(res.error); } 
      } catch (error) { this.mostrarError(error.message); } 
    },
    // ============ FIN RECÁLCULO INDIVIDUAL ============

    // ============ RECÁLCULO MASIVO CORREGIDO ============
    /**
     * Recalcula los precios de todos los productos filtrados.
     * ✅ REGLA: El costo real es en USD. El BCV NO entra en el costo.
     * ✅ REGLA DE PISO: El precio en USD NUNCA baja del anterior.
     * ✅ El BCV solo se usa para convertir USD → Bs.
     */
    async recalcularPreciosMasivos() { 
      this.loading = true; 
      let exitosos = 0, fallidos = 0; 
      const tasaBCV = this.tasaBCV?.precio_usd_bs || 1; 
      
      for (const p of this.filteredProductos) { 
        try { 
          // ✅ Costo REAL en USD (USDT). El BCV NO entra en el costo.
          const costoRealUSD = parseFloat(p.precio_compra_usdt); 
          
          // ✅ Precio de venta en USD = costo + margen
          let precioVentaUSD;
          if (p.tipo_ganancia === 'PORCENTAJE') { 
            precioVentaUSD = costoRealUSD * (1 + ((p.valor_ganancia || 30) / 100)); 
          } else { 
            precioVentaUSD = costoRealUSD + (p.valor_ganancia || 15); 
          } 
          
          // ✅ REGLA DE PISO: el precio NUNCA baja del anterior
          if (p.precio_venta_usd > 0 && precioVentaUSD < p.precio_venta_usd) {
            precioVentaUSD = p.precio_venta_usd;
          }
          
          const nuevoPrecioUSD = Math.round(precioVentaUSD * 100) / 100; 
          const nuevoPrecioBs = Math.round(nuevoPrecioUSD * tasaBCV); 
          
          const res = await actualizarProducto(p.idProducto, { 
            ...p, 
            precio_venta_usd: nuevoPrecioUSD, 
            precio_venta_bs: nuevoPrecioBs 
          }); 
          res.success ? exitosos++ : fallidos++; 
        } catch (error) { fallidos++; } 
      } 
      await this.loadData(); 
      this.loading = false; 
      if (fallidos > 0) this.mostrarAdvertencia(`✅ ${exitosos} ok, ❌ ${fallidos} error`); 
      else this.mostrarExito(`✅ ${exitosos} actualizados`); 
    },
    // ============ FIN RECÁLCULO MASIVO ============

    async saveStock() { 
      if (!this.stockForm.cantidad || this.stockForm.cantidad <= 0) { this.mostrarError('Cantidad válida'); return; } 
      this.savingStock = true; 
      try { 
        let cantidad = parseFloat(this.stockForm.cantidad); 
        if (this.stockForm.operacion === 'disminuir') cantidad = -cantidad; 
        const res = await actualizarStock(this.stockProducto.idProducto, cantidad, this.stockForm.operacion); 
        if (res.success) { await this.loadData(); this.closeStockModal(); this.mostrarExito('Stock actualizado'); } 
        else { throw new Error(res.error); } 
      } catch (error) { this.mostrarError(error.message); } 
      finally { this.savingStock = false; } 
    },
    clearFilters() { 
      this.searchQuery = ''; 
      this.filterStatus = 'all'; 
      this.filterStock = 'all'; 
      this.filterProveedor = 'all'; 
      this.sortBy = 'nombre'; 
      this.currentPage = 1; 
      this.loadData(); 
    },
  },
};
</script>

<style>
/* Solución global para los <option> en los modales teletransportados al body */
.dark body select option {
  background-color: #1f2937 !important;
  color: #f9fafb !important;
}
body select option {
  background-color: #ffffff;
  color: #111827;
}
</style>

<style scoped>
/* ========== ESTILOS COMPACTOS ========== */
.productos-container {
  padding: 0.5rem;
}

@media (min-width: 768px) {
  .productos-container {
    padding: 0.75rem;
  }
}

.header-section {
  margin-bottom: 0.75rem;
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
}

@media (min-width: 768px) {
  .header-section {
    flex-direction: row;
    align-items: center;
  }
}

.header-title h1 {
  font-size: 1.25rem;
  font-weight: bold;
  color: #1f2937;
  margin: 0;
}

.dark .header-title h1 {
  color: #ffffff;
}

.header-title p {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
}

.header-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

@media (min-width: 640px) {
  .header-buttons {
    flex-direction: row;
    width: auto;
  }
}

.btn-recalcular, .btn-nuevo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.3s;
  border: none;
  cursor: pointer;
}

.btn-recalcular {
  background: linear-gradient(135deg, #d97706, #ea580c);
  color: white;
}

.btn-recalcular:hover {
  background: linear-gradient(135deg, #b45309, #c2410c);
}

.btn-nuevo {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
}

.btn-nuevo:hover {
  background: linear-gradient(135deg, #1d4ed8, #4338ca);
}

.filtros-card {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  border: 1px solid #e5e7eb;
}

.dark .filtros-card {
  background-color: #1f2937;
  border-color: #374151;
}

.filtros-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

@media (min-width: 768px) {
  .filtros-grid {
    grid-template-columns: repeat(12, 1fr);
  }
  .filtro-buscar {
    grid-column: span 12;
  }
}
@media (min-width: 1024px) {
  .filtro-buscar {
    grid-column: span 4;
  }
  .filtro-select {
    grid-column: span 2;
  }
}

.filtro-buscar label, .filtro-select label {
  display: block;
  font-size: 0.7rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: #374151;
}

.dark .filtro-buscar label, .dark .filtro-select label {
  color: #d1d5db;
}

.input-icon {
  position: relative;
}

.input-icon .icon-left {
  position: absolute;
  left: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 0.875rem;
}

.input-icon input {
  width: 100%;
  padding: 0.375rem 0.5rem 0.375rem 2rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: white;
  font-size: 0.75rem;
}

.dark .input-icon input {
  background-color: #374151;
  border-color: #4b5563;
  color: #f3f4f6;
}

.filtro-select select {
  width: 100%;
  padding: 0.375rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: white;
  font-size: 0.75rem;
}

.dark .filtro-select select {
  background-color: #374151;
  border-color: #4b5563;
  color: #f3f4f6;
}

.filtro-select select option {
  background-color: #ffffff;
  color: #374151;
}

.dark .filtro-select select option {
  background-color: #374151;
  color: #f3f4f6;
}

.tasas-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

@media (min-width: 768px) {
  .tasas-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.tasa-card {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid;
}

.tasa-usdt {
  background: linear-gradient(135deg, #fffbeb, #fff7ed);
  border-color: #fed7aa;
}

.tasa-bcv {
  background: linear-gradient(135deg, #eff6ff, #eef2ff);
  border-color: #bfdbfe;
}

.tasa-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tasa-icon {
  padding: 0.25rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tasa-usdt .tasa-icon {
  background-color: #d97706;
}

.tasa-bcv .tasa-icon {
  background-color: #2563eb;
}

.tasa-icon svg {
  color: white;
  font-size: 0.875rem !important;
}

.tasa-header p {
  font-size: 0.7rem;
  font-weight: 500;
  color: #4b5563;
  margin: 0;
}

.tasa-valor {
  font-size: 0.875rem;
  font-weight: bold;
  color: #111827;
  margin: 0;
}

.tasa-footer {
  margin-top: 0.25rem;
  display: flex;
  justify-content: space-between;
  font-size: 0.625rem;
  color: #6b7280;
}

.productos-card {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.dark .productos-card {
  background-color: #1f2937;
  border-color: #374151;
}

.productos-header {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.5rem;
}

@media (min-width: 640px) {
  .productos-header {
    flex-direction: row;
    align-items: center;
  }
}

.productos-header h2 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.productos-header p {
  font-size: 0.625rem;
  color: #6b7280;
  margin: 0;
}

.productos-grid {
  padding: 0.5rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

@media (min-width: 640px) {
  .productos-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .productos-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.producto-card {
  background-color: white;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: all 0.3s;
}

.dark .producto-card {
  background-color: #1f2937;
  border-color: #374151;
}

.producto-card:hover {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transform: translateY(-1px);
}

.producto-imagen {
  position: relative;
  height: 6rem;
  background: linear-gradient(135deg, #f9fafb, #f3f4f6);
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (min-width: 640px) {
  .producto-imagen {
    height: 7rem;
  }
}

@media (min-width: 768px) {
  .producto-imagen {
    height: 8rem;
  }
}

.estado-badge {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  padding: 0.125rem 0.375rem;
  font-size: 0.625rem;
  font-weight: 600;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  gap: 0.125rem;
  cursor: pointer;
  z-index: 10;
}

.estado-activo {
  background-color: #d1fae5;
  color: #065f46;
}

.estado-inactivo {
  background-color: #fee2e2;
  color: #991b1b;
}

.imagen-contenedor {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}

.imagen-contenedor img {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  border-radius: 0.25rem;
}

.imagen-contenedor svg {
  font-size: 2rem;
  color: #d1d5db;
}

.stock-badge {
  position: absolute;
  bottom: 0.25rem;
  left: 0.25rem;
  padding: 0.125rem 0.375rem;
  font-size: 0.625rem;
  font-weight: 600;
  border-radius: 9999px;
}

.stock-cero {
  background-color: #fee2e2;
  color: #991b1b;
}

.stock-bajo {
  background-color: #fed7aa;
  color: #92400e;
}

.stock-normal {
  background-color: #d1fae5;
  color: #065f46;
}

.producto-info {
  padding: 0.5rem;
}

.producto-codigo {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
  font-size: 0.625rem;
  background-color: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 9999px;
  color: #4b5563;
}

.categoria-badge {
  background-color: #dbeafe;
  color: #1e40af;
  padding: 0.0625rem 0.375rem;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  font-size: 0.625rem;
}

.producto-info h3 {
  font-size: 0.75rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.125rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.producto-info > p {
  font-size: 0.625rem;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

.precios {
  margin-bottom: 0.5rem;
  font-size: 0.625rem;
}

.precios > div {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.125rem;
}

.precios > div > span:first-child {
  color: #4b5563;
}

.precio-venta {
  font-weight: bold;
  color: #10b981;
  font-size: 0.75rem;
}

.precios small {
  font-size: 0.5rem;
  display: block;
  color: #6b7280;
}

.ganancia-positiva {
  color: #10b981;
}

.ganancia-negativa {
  color: #ef4444;
}

.margen {
  background-color: #f9fafb;
  padding: 0.25rem;
  border-radius: 0.25rem;
  margin-top: 0.125rem;
}

.margen span:last-child {
  font-weight: 600;
  color: #3b82f6;
}

.producto-detalles {
  margin-bottom: 0.5rem;
  font-size: 0.625rem;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.producto-detalles div {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.producto-acciones {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
}

.producto-acciones button {
  padding: 0.25rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.producto-acciones button svg {
  font-size: 0.875rem;
}

.btn-editar {
  background-color: #eff6ff;
  color: #2563eb;
}

.btn-stock {
  background-color: #f9fafb;
  color: #6b7280;
}

.btn-calcular {
  background-color: #fffbeb;
  color: #d97706;
}

.btn-eliminar {
  background-color: #fef2f2;
  color: #dc2626;
}

.paginacion {
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

@media (min-width: 640px) {
  .paginacion {
    flex-direction: row;
  }
}

.paginacion-botones {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.paginacion-botones button {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  background-color: white;
  cursor: pointer;
  font-size: 0.75rem;
}

.paginacion-botones button.active {
  background-color: #2563eb;
  color: white;
  border-color: #2563eb;
}

.paginacion-botones button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}

.modal-container {
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 56rem;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dark .modal-container {
  background-color: #1f2937;
}

.modal-header {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  padding: 0.5rem 0.75rem;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-header-icon {
  padding: 0.25rem;
  background-color: rgba(255,255,255,0.2);
  border-radius: 0.5rem;
}

.modal-header-icon svg {
  color: white;
  font-size: 0.875rem;
}

.modal-header-content h3 {
  font-size: 0.875rem;
  font-weight: bold;
  color: white;
  margin: 0;
}

.modal-header-content p {
  font-size: 0.625rem;
  color: #bfdbfe;
  margin: 0;
}

.modal-close {
  padding: 0.25rem;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 0.375rem;
}

.modal-close svg {
  color: white;
  font-size: 0.875rem;
}

.modal-form {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

.modal-body {
  overflow-y: auto;
  padding: 0.75rem;
  flex: 1;
  min-height: 0;
}

.modal-footer {
  padding: 0.5rem 0.75rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
  background-color: white;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  margin-bottom: 0;
}

@media (min-width: 640px) {
  .form-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

.form-group {
  margin-bottom: 0.5rem;
}

.form-group label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.125rem;
}

.required {
  color: #ef4444;
}

.form-group input, .form-group select, .form-group textarea {
  width: 100%;
  padding: 0.375rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: white;
  font-size: 0.75rem;
}

.readonly-input {
  background-color: #f3f4f6;
  font-weight: bold;
  cursor: not-allowed;
}

.error-text {
  font-size: 0.625rem;
  color: #ef4444;
  margin-top: 0.125rem;
}

.imagen-upload {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}

@media (min-width: 640px) {
  .imagen-upload {
    flex-direction: row;
    align-items: center;
  }
}

.imagen-preview {
  width: 4rem;
  height: 4rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #f3f4f6;
  border: 1px dashed #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.imagen-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.imagen-preview svg {
  font-size: 1.5rem;
  color: #9ca3af;
}

.btn-upload {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 500;
  background-color: white;
}

.btn-upload input {
  display: none;
}

.btn-remove-img {
  margin-top: 0.25rem;
  padding: 0.125rem 0.25rem;
  background: none;
  border: none;
  color: #ef4444;
  font-size: 0.625rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
}

.radio-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  font-weight: normal;
  font-size: 0.75rem;
}

.radio-group input {
  width: auto;
  margin: 0;
}

.upload-progress {
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner-small {
  width: 0.75rem;
  height: 0.75rem;
  border: 2px solid #3b82f6;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.progress-bar {
  flex: 1;
  height: 0.25rem;
  background-color: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #3b82f6;
  transition: width 0.3s;
}

.btn-cancelar, .btn-guardar {
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.75rem;
  border: none;
}

.btn-cancelar {
  border: 1px solid #d1d5db;
  background-color: white;
}

.btn-guardar {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.btn-guardar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* --- ESTILOS MODERNOS Y PREMIUM (MINIMALISTA) --- */
.modern-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background-color: #ffffff;
  font-size: 0.875rem;
  color: #0f172a;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.dark .modern-input {
  background-color: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

.modern-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.modern-input option {
  background-color: #ffffff;
  color: #0f172a;
}

.dark .modern-input option {
  background-color: #0f172a;
  color: #f1f5f9;
}

.form-section {
  padding: 1.5rem;
  background-color: transparent;
}

.form-section h4 {
  color: #475569;
  letter-spacing: 0.05em;
  font-size: 0.75rem;
}

.dark .form-section h4 {
  color: #94a3b8;
}

.premium-image-upload {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  padding: 1.25rem;
  background-color: #f8fafc;
  border-radius: 1.25rem;
  border: 2px dashed #cbd5e1;
  transition: all 0.3s ease;
}

.dark .premium-image-upload {
  background-color: #1e293b;
  border-color: #475569;
}

.premium-image-upload:hover {
  border-color: #3b82f6;
  background-color: #f1f5f9;
  transform: scale(1.01);
}

.preview-container {
  width: 7.5rem;
  height: 7.5rem;
  border-radius: 1rem;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #ffffff;
}

.dark .preview-container {
  background-color: #334155;
  border-color: #475569;
}

.preview-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.empty-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.5;
}

.controls {
  display: flex;
  gap: 0.75rem;
}

.upload-btn-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #1e293b;
  color: white;
  border-radius: 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.dark .upload-btn-label {
  background-color: #3b82f6;
}

.upload-btn-label:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
}

.remove-btn {
  padding: 0.5rem;
  background-color: #fff1f2;
  color: #e11d48;
  border-radius: 0.75rem;
  border: 1px solid #ffe4e6;
  transition: all 0.2s;
}

.dark .remove-btn {
  background-color: #881337;
  color: #fff1f2;
  border-color: #9f1239;
}

.remove-btn:hover {
  background-color: #ffe4e6;
  transform: scale(1.1);
}

.modal-stock {
  background-color: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 28rem;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header-stock {
  background: linear-gradient(135deg, #f59e0b, #f97316);
  padding: 0.5rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header-stock > div {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.modal-header-stock svg {
  color: white;
  font-size: 0.875rem;
}

.modal-header-stock h3 {
  font-size: 0.875rem;
  font-weight: bold;
  color: white;
  margin: 0;
}

.modal-header-stock p {
  font-size: 0.625rem;
  color: #fed7aa;
  margin: 0;
}

.modal-stock form {
  padding: 0.75rem;
  overflow-y: auto;
}

.stock-actual {
  background-color: #f9fafb;
  padding: 0.375rem;
  border-radius: 0.5rem;
  margin-bottom: 0.375rem;
  text-align: center;
  font-size: 0.7rem;
}

.stock-actual strong {
  font-size: 1rem;
  font-weight: bold;
  margin-left: 0.25rem;
}

.modal-confirm {
  background-color: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 28rem;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.confirm-header {
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.confirm-header svg {
  font-size: 1rem;
  padding: 0.25rem;
  background-color: rgba(255,255,255,0.2);
  border-radius: 9999px;
}

.confirm-header h3 {
  font-size: 0.875rem;
  font-weight: bold;
  color: white;
  margin: 0;
}

.confirm-header p {
  font-size: 0.625rem;
  color: rgba(255,255,255,0.9);
  margin: 0;
}

.confirm-body {
  padding: 0.5rem 0.75rem;
}

.confirm-body p {
  font-size: 0.75rem;
  color: #374151;
  margin: 0;
}

.confirm-detalle {
  font-size: 0.625rem;
  color: #6b7280;
  margin-top: 0.25rem !important;
}

.confirm-footer {
  padding: 0.5rem 0.75rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.confirm-footer button {
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.75rem;
  border: none;
}

.confirm-footer button:first-child {
  border: 1px solid #d1d5db;
  background-color: white;
}

.confirm-danger .confirm-header {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}

.confirm-warning .confirm-header {
  background: linear-gradient(135deg, #f97316, #ea580c);
}

.confirm-success .confirm-header {
  background: linear-gradient(135deg, #10b981, #059669);
}

.confirm-blue .confirm-header {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.confirm-amber .confirm-header {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.btn-danger {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  color: white;
}

.btn-warning {
  background: linear-gradient(135deg, #f97316, #ea580c);
  color: white;
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-blue {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.btn-amber {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.loading-spinner {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #3b82f6;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  padding: 1.5rem;
  text-align: center;
}

.empty-state svg {
  font-size: 2rem;
  color: #9ca3af;
  margin-bottom: 0.5rem;
}

.empty-state h3 {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.empty-state p {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.empty-state button {
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  border: none;
  background-color: #2563eb;
  color: white;
}

.dark .empty-state h3 {
  color: #f3f4f6;
}

.dark .empty-state p {
  color: #9ca3af;
}

.line-clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Estilos para el lector de código */
.lector-input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  border-color: #3b82f6;
}

@keyframes pulse-border {
  0%, 100% { border-color: #3b82f6; }
  50% { border-color: #93c5fd; }
}

.lector-input:focus {
  animation: pulse-border 1.5s ease-in-out infinite;
}
</style>